import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import template from './statistics-view.html';
import { getStatistics, formatFileSize } from '../services/api.service';
import type { StatisticsResponse } from '../services/api.types';

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  Filler,
);

@customElement({ name: 'statistics-view', template })
export class StatisticsView {
  private readonly i18n: I18N = resolve(I18N);
  private donutCanvas!: HTMLCanvasElement;
  private typeCanvas!: HTMLCanvasElement;
  private histogramCanvas!: HTMLCanvasElement;
  private growthCanvas!: HTMLCanvasElement;
  private sizeGrowthCanvas!: HTMLCanvasElement;

  private _donutChart: Chart | null = null;
  private _typeChart: Chart | null = null;
  private _histogramChart: Chart | null = null;
  private _growthChart: Chart | null = null;
  private _sizeGrowthChart: Chart | null = null;
  private _data: StatisticsResponse | null = null;

  public isLoading = true;
  public error = '';

  public async attached(): Promise<void> {
    try {
      this._data = await getStatistics();
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Failed to load statistics';
    } finally {
      this.isLoading = false;
    }

    if (this._data && !this.error) {
      requestAnimationFrame(() => {
        if (!this._data) return;
        this.renderDonut(this._data);
        this.renderTypeDonut(this._data);
        this.renderHistogram(this._data);
        this.renderGrowth(this._data);
        this.renderSizeGrowth(this._data);
      });
    }
  }

  public detaching(): void {
    this._donutChart?.destroy();
    this._typeChart?.destroy();
    this._histogramChart?.destroy();
    this._growthChart?.destroy();
    this._sizeGrowthChart?.destroy();
  }

  public get totalFilesSize(): string {
    return formatFileSize(this._data?.fileSizeStats.totalFilesSizeBytes);
  }

  public get totalPreviewSize(): string {
    return formatFileSize(this._data?.fileSizeStats.totalPreviewSizeBytes);
  }

  public get ownFilesSize(): string {
    return formatFileSize(this._data?.fileSizeStats.ownFilesSizeBytes);
  }

  public get otherFilesSize(): string {
    return formatFileSize(this._data?.fileSizeStats.otherFilesSizeBytes);
  }

  public get avgPdfSize(): string {
    return formatFileSize(this._data?.fileSizeStats.avgPdfSizeBytes);
  }

  public get avgImageSize(): string {
    return formatFileSize(this._data?.fileSizeStats.avgImageSizeBytes);
  }

  public get ownFilesSizePercent(): number {
    const stats = this._data?.fileSizeStats;
    if (!stats || stats.totalFilesSizeBytes === 0) return 0;
    return Math.round((stats.ownFilesSizeBytes / stats.totalFilesSizeBytes) * 100);
  }

  public get totalPageCount(): number {
    return this._data?.fileSizeStats.totalPageCount ?? 0;
  }

  public get avgPdfPageCount(): string {
    const pages = this._data?.fileSizeStats.avgPdfPageCount;
    return pages != null ? pages.toFixed(1) : '0';
  }

  private renderDonut(data: StatisticsResponse): void {
    const labels = ['Own', ...data.sharers.map(s => s.displayName)];
    const values = [data.ownCount, ...data.sharers.map(s => s.count)];
    const colors = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    this._donutChart = new Chart(this.donutCanvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: colors.slice(0, labels.length), borderWidth: 2 }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right' },
          tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}` } },
        },
      },
    });
  }

  private renderTypeDonut(data: StatisticsResponse): void {
    const labels = [
      this.i18n.tr('statistics.type.real'),
      this.i18n.tr('statistics.type.formOnly'),
    ];

    this._typeChart = new Chart(this.typeCanvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data: [data.realDocumentCount, data.formOnlyDocumentCount],
          backgroundColor: ['#6366f1', '#f59e0b'],
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right' },
          tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}` } },
        },
      },
    });
  }

  private renderHistogram(data: StatisticsResponse): void {
    this._histogramChart = new Chart(this.histogramCanvas, {
      type: 'bar',
      data: {
        labels: data.documentDateHistogram.map(y => String(y.year)),
        datasets: [
          {
            label: this.i18n.tr('statistics.type.real'),
            data: data.documentDateHistogram.map(y => y.realCount),
            backgroundColor: '#6366f1',
            borderRadius: 4,
          },
          {
            label: this.i18n.tr('statistics.type.formOnly'),
            data: data.documentDateHistogram.map(y => y.formOnlyCount),
            backgroundColor: '#f59e0b',
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true } },
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }

  private renderGrowth(data: StatisticsResponse): void {
    this._growthChart = new Chart(this.growthCanvas, {
      type: 'line',
      data: {
        labels: data.growth.map(g => g.label),
        datasets: [
          {
            label: this.i18n.tr('statistics.type.real'),
            data: data.growth.map(g => g.realCumulative),
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: this.i18n.tr('statistics.type.formOnly'),
            data: data.growth.map(g => g.formOnlyCumulative),
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245,158,11,0.1)',
            fill: false,
            tension: 0.3,
            pointRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }

  private renderSizeGrowth(data: StatisticsResponse): void {
    this._sizeGrowthChart = new Chart(this.sizeGrowthCanvas, {
      type: 'line',
      data: {
        labels: data.fileSizeGrowth.map(g => g.label),
        datasets: [
          {
            label: 'Document files',
            data: data.fileSizeGrowth.map(g => g.filesCumulativeBytes),
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: 'Previews',
            data: data.fileSizeGrowth.map(g => g.previewsCumulativeBytes),
            borderColor: '#22c55e',
            backgroundColor: 'rgba(34,197,94,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true },
          tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${formatFileSize(ctx.parsed.y)}` } },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: value => formatFileSize(Number(value)) },
          },
        },
      },
    });
  }
}
